import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  ArrowLeft,
  RefreshCw,
  Activity,
  Zap,
  Loader2,
  ListFilter,
} from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import {
  getNaerasJobRunsAsync,
  getNaerasPoolAsync,
  getNaerasTodayJobRunsAsync,
} from '@/data/store/actions';
import {
  getNaerasJobRuns,
  getNaerasJobRunsCursor,
  getNaerasPool,
  getNaerasTodayJobRuns,
} from '@/data/store/selectors';
import { TNaerasJob, TNaerasJobRun, TNaerasJobStatus } from '@/data/types';
import useNaerasPerk from '@/hooks/useNaerasPerk';
import PoolStatusCard from './components/PoolStatusCard';
import PoolEditDialog from './components/PoolEditDialog';
import TodayJobCards from './components/TodayJobCards';
import JobRunRow from './components/JobRunRow';
import { JOB_META, JOB_ORDER, STATUS_FILTERS } from './utils/jobs';
import { athensToday } from './utils/pool';

const PAGE_SIZE = 25;

type JobFilter = TNaerasJob | 'all';
type StatusFilter = TNaerasJobStatus | 'all';

export const JobRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const hasNaerasPerk = useNaerasPerk();
  const jobRuns = useSelector(getNaerasJobRuns);
  const todayRuns = useSelector(getNaerasTodayJobRuns);
  const cursor = useSelector(getNaerasJobRunsCursor);
  const pool = useSelector(getNaerasPool);

  const [jobFilter, setJobFilter] = useState<JobFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPoolEditOpen, setIsPoolEditOpen] = useState(false);

  /**
   * Newest first, so the answer to "did today run?" is at the top. Passing a
   * cursor appends; omitting it starts the list over.
   */
  const fetchRuns = useCallback(
    (nextCursor?: string) => {
      const params = {
        page_size: PAGE_SIZE,
        sort_dir: 'DESC' as const,
        ...(jobFilter !== 'all' ? { job: jobFilter } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(nextCursor ? { cursor: nextCursor } : {}),
      };

      return dispatch(getNaerasJobRunsAsync({ params }))
        .unwrap()
        .catch(() => undefined);
    },
    [dispatch, jobFilter, statusFilter]
  );

  /** The status cards and the pool — independent of the table's filters. */
  const fetchOverview = useCallback(
    () =>
      Promise.all([
        dispatch(getNaerasTodayJobRunsAsync())
          .unwrap()
          .catch(() => undefined),
        dispatch(getNaerasPoolAsync())
          .unwrap()
          .catch(() => undefined),
      ]),
    [dispatch]
  );

  useEffect(() => {
    if (!hasNaerasPerk) return;

    fetchOverview();
  }, [hasNaerasPerk, fetchOverview]);

  useEffect(() => {
    if (!hasNaerasPerk) return;

    setIsLoading(true);
    fetchRuns().finally(() => setIsLoading(false));
  }, [hasNaerasPerk, fetchRuns]);

  const handleLoadMore = async () => {
    if (!cursor || isLoadingMore) return;

    setIsLoadingMore(true);
    await fetchRuns(cursor);
    setIsLoadingMore(false);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await Promise.all([fetchRuns(), fetchOverview()]);
    setIsLoading(false);
  };

  // Today on the Greek calendar, which is what `run_date` counts in. Not the
  // pool's `as_of`: while the pool is stale that is an older day.
  const todayStr = athensToday();

  const runsToday = useMemo(() => {
    const map: Partial<Record<TNaerasJob, TNaerasJobRun>> = {};

    todayRuns.forEach((run) => {
      if (run.run_date === todayStr && !map[run.job]) {
        map[run.job] = run;
      }
    });

    return map;
  }, [todayRuns, todayStr]);

  if (hasNaerasPerk === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>
    );
  }

  if (hasNaerasPerk === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Κατάσταση Εργασιών NAERAS</h2>
          <p className="text-gray-500">
            This feature requires the NAERAS add-on. Please contact your organization administrator or Enexan support to enable this feature for your account.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-xl transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/naeras/hybrid')}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition shadow-xs"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Υπολογισμός Προσφοράς
              </button>
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold text-blue-800">
                Παρακολούθηση
              </span>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-gray-900">Κατάσταση Εργασιών NAERAS</h1>
            <p className="mt-1 text-base text-gray-600">
              Παρακολούθηση των αυτόματων εργασιών που τροφοδοτούν τον υπολογισμό της προσφοράς.
            </p>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </button>
        </div>

        {/* Hybrid pool — the number every bid is capped by */}
        <section>
          <PoolStatusCard pool={pool} isLoading={isLoading} onEdit={() => setIsPoolEditOpen(true)} />
        </section>

        <PoolEditDialog
          isOpen={isPoolEditOpen}
          onClose={() => setIsPoolEditOpen(false)}
          onChanged={() => {
            fetchOverview();
          }}
        />

        {/* Today at a glance */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Activity className="h-4 w-4 flex-shrink-0 text-blue-600" />
            <h2 className="text-sm font-bold text-gray-800">
              Σήμερα, {format(new Date(`${todayStr}T00:00:00`), 'dd/MM/yyyy')}
            </h2>
            {/* Said once here, so four narrow cards don't each repeat it. */}
            <span className="text-xs text-gray-500">· ώρες Ελλάδας</span>
          </div>
          <TodayJobCards runsToday={runsToday} isLoading={isLoading} todayStr={todayStr} />
        </section>

        {/* Section Separator */}
        <div className="my-10">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <h2 className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-lg font-bold text-gray-800 shadow-sm">
              Ιστορικό Εκτελέσεων
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-600">
            <ListFilter className="h-4 w-4 text-blue-600" />
            <span>Φίλτρα:</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold text-gray-500">Εργασία</span>
            {(['all', ...JOB_ORDER] as JobFilter[]).map((value) => (
              <button
                key={value}
                onClick={() => setJobFilter(value)}
                title={value === 'all' ? 'Όλες οι εργασίες' : JOB_META[value].title}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition sm:px-3.5 ${
                  jobFilter === value
                    ? 'bg-indigo-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {value === 'all' ? (
                  'Όλες'
                ) : (
                  <>
                    {value}
                    {/* Four job names plus the status row overflow a single
                        line below this width — the letter alone still reads,
                        and the tooltip carries the name. */}
                    <span className="hidden lg:inline"> · {JOB_META[value].title}</span>
                  </>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold text-gray-500">Κατάσταση</span>
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                  statusFilter === option.value
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Runs table */}
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5">
          <div className="mb-4 border-b border-gray-100 pb-4">
            <h3 className="text-lg font-bold text-gray-900">Εκτελέσεις Εργασιών</h3>
            <p className="text-xs text-gray-500">
              Μία γραμμή ανά εργασία και ημέρα — οι επαναλήψεις της ίδιας ημέρας αυξάνουν τις
              προσπάθειες. Πατήστε μια γραμμή για λεπτομέρειες.
            </p>
          </div>

          {isLoading && jobRuns.length === 0 ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((row) => (
                <div key={row} className="h-11 animate-pulse rounded-xl bg-gray-100" />
              ))}
            </div>
          ) : jobRuns.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm font-semibold text-gray-700">Δεν βρέθηκαν εκτελέσεις</p>
              <p className="mt-1 text-xs text-gray-500">
                Δοκιμάστε να αφαιρέσετε τα φίλτρα ή ελέγξτε ξανά αργότερα.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-gray-200 bg-gray-50/70 font-semibold text-gray-600">
                  <tr>
                    <th className="py-3 px-4">Ημερομηνία</th>
                    <th className="py-3 px-4">Εργασία</th>
                    <th className="py-3 px-4 text-center">Κατάσταση</th>
                    <th className="py-3 px-4 text-center">Προσπάθειες</th>
                    <th className="py-3 px-4 text-right">Έναρξη (Ελλάδας)</th>
                    <th className="py-3 px-4 text-right">Ενημέρωση</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {jobRuns.map((run) => (
                    <JobRunRow key={`${run.job}-${run.run_date}${run.manual ? '-manual' : ''}`} run={run} todayStr={todayStr} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {cursor && (
            <div className="mt-5 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-5 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-100 disabled:opacity-60"
              >
                {isLoadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Φόρτωση περισσότερων
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default JobRunsPage;
